import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, BRAND } from "@/lib/siteData";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const goToSection = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (pathname === "/") {
      const el = document.getElementById(id);
      if (el) (window.__lenis?.scrollTo?.(el, { offset: -80 })) || el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-[9000] transition-[background-color,backdrop-filter,border-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-void/95 backdrop-blur-xl border-b border-white/10 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.9)]"
          : "bg-gradient-to-b from-void/80 via-void/40 to-transparent border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20 md:h-24">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <img src={BRAND.logo} alt="Vivek Parekh Production" className="h-10 md:h-14 w-auto transition-opacity duration-300 group-hover:opacity-90" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <a
              key={item.id}
              href={`/#${item.id}`}
              onClick={goToSection(item.id)}
              data-testid={`nav-${item.id}`}
              className="relative text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-gold text-offwhite/80 cursor-pointer"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href="/#contact"
            onClick={goToSection("contact")}
            data-testid="header-start-project"
            className="hidden md:inline-flex items-center border border-gold text-gold px-6 py-3 text-xs uppercase tracking-[0.2em] overflow-hidden relative group cursor-pointer"
          >
            <span className="relative z-10 group-hover:text-void transition-colors duration-500">Start a Project</span>
            <span className="absolute inset-0 bg-gold origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-offwhite p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden bg-void/95 backdrop-blur-2xl border-t border-white/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <nav className="container-x py-6 flex flex-col gap-1">
              {NAV.map((item) => (
                <a
                  key={item.id}
                  href={`/#${item.id}`}
                  onClick={goToSection(item.id)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="py-3 font-display text-2xl tracking-wide border-b border-white/5 text-offwhite cursor-pointer"
                >
                  {item.label}
                </a>
              ))}
              <a href="/#contact" onClick={goToSection("contact")} data-testid="mobile-start-project" className="mt-4 text-center border border-gold text-gold py-4 uppercase tracking-[0.2em] text-xs cursor-pointer">
                Start a Project
              </a>
              <a href={BRAND.instagramUrl} target="_blank" rel="noreferrer" className="mt-2 text-center text-gold/70 text-xs tracking-[0.2em] uppercase py-2">
                {BRAND.instagramHandle}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
